import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { CellNoticeTone } from "@/components/ixd-table";
import {
  normalizeCellTagKey,
  periodScopedTagKey,
  stripAccumulatedType,
} from "@/utils/workbook/cell-tag-key";

/**
 * 워크북 셀 주소(`sourceDsdAddress`) → 그 셀에 알릴 사유.
 *
 * 값 동기화 실패(failures)는 서버가 대응 IXD 셀을 못 찾아 실패한 것이라 IXD
 * 쪽에는 붙일 자리가 없다. 워크북 셀은 주소로 특정되므로 여기에 표시한다 —
 * 고쳐야 할 자리도 워크북이다.
 *
 * 표는 XBRLPreview → TableWithCopy → TableCell 로 네 홉을 거친다. 그 사이
 * 컴포넌트들은 이 정보와 아무 상관이 없어 prop으로 흘리지 않고 context로 준다.
 */
/** 사유 문구와 표식 색. 색은 원인이 다르면 갈라야 사용자가 엉뚱한 곳을 안 뒤진다. */
export interface WorkbookCellNotice {
  /**
   * 이 사유를 만든 진단 행의 키. 셀은 이것을 `data-notice-key`로 달고,
   * 반영불가 패널은 같은 키로 그 셀을 찾아 사용자를 데려간다.
   */
  key: string;
  reason: string;
  /** 셀 하단에 상주하는 짧은 사유. 누르면 위 reason 전문이 패널로 뜬다 */
  shortReason: string;
  tone: CellNoticeTone;
}

export type WorkbookCellNotices = ReadonlyMap<string, WorkbookCellNotice>;

interface WorkbookCellNoticeConfig {
  /** 워크북 셀 주소 → 사유 */
  notices?: WorkbookCellNotices;
  /**
   * `{기간}::{태그 키}` → 사유.
   *
   * 서버가 셀 주소를 못 주는 실패가 있다. 그때도 셀은 자기 태그 키를
   * `data-cell-id`로 들고 있으므로 그쪽으로 짚는다. TaggedKey는 기간을 담지
   * 않아 당기·전기가 같은 키가 되므로 기간을 반드시 묶는다.
   */
  noticesByTagKey?: WorkbookCellNotices;
  /** 알림이 붙은 셀을 눌렀을 때. 화면이 자기 패널을 띄운다 */
  onNoticeClick?: (notice: WorkbookCellNotice, element: HTMLElement) => void;
}

const WorkbookCellNoticeContext = createContext<WorkbookCellNoticeConfig>({});

export const WorkbookCellNoticeProvider = ({
  notices,
  noticesByTagKey,
  onNoticeClick,
  children,
}: WorkbookCellNoticeConfig & { children: ReactNode }) => {
  const value = useMemo(
    () => ({ notices, noticesByTagKey, onNoticeClick }),
    [notices, noticesByTagKey, onNoticeClick],
  );
  return (
    <WorkbookCellNoticeContext.Provider value={value}>
      {children}
    </WorkbookCellNoticeContext.Provider>
  );
};

/**
 * 이 셀에 알릴 것이 있으면 notice, 없으면 undefined.
 *
 * 셀 주소를 먼저 본다 — 서버가 그 셀이라고 지목한 것이라 가장 확실하다.
 * 주소가 없는 실패만 태그 키로 찾는다.
 */
export const useWorkbookCellNotice = (
  sourceDsdAddress: string | null | undefined,
  cellTagKey?: string | null,
  period?: string,
) => {
  const { notices, noticesByTagKey, onNoticeClick } = useContext(
    WorkbookCellNoticeContext,
  );
  const byAddress = sourceDsdAddress
    ? notices?.get(sourceDsdAddress)
    : undefined;
  const byTagKey =
    !byAddress && cellTagKey && period && noticesByTagKey?.size
      ? (noticesByTagKey.get(
          periodScopedTagKey(period, normalizeCellTagKey(cellTagKey)),
        ) ??
        noticesByTagKey.get(
          periodScopedTagKey(
            period,
            stripAccumulatedType(normalizeCellTagKey(cellTagKey)),
          ),
        ))
      : undefined;
  return { notice: byAddress ?? byTagKey, onNoticeClick };
};
